import { useState, useEffect } from 'react';
import SettingsPageShell from './SettingsPageShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { retailerSettings } from '../../utils/api';
import { toast } from 'sonner';
import { Save, Users, Truck, Clock } from 'lucide-react';

export default function RetailerPickupDelivery({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    retailerSettings.getPickupDelivery().then((res) => setData(res.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const setField = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await retailerSettings.updatePickupDelivery(data);
      toast.success('Pickup-Delivery settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <SettingsPageShell user={user} onLogout={onLogout} title="Pickup–Delivery"><div className="text-gray-500">Loading…</div></SettingsPageShell>;

  return (
    <SettingsPageShell
      user={user}
      onLogout={onLogout}
      title="Pickup–Delivery"
      subtitle="Help consumers and drivers reach you smoothly"
      actions={
        <Button onClick={save} disabled={saving} data-testid="pickup-delivery-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      }
    >
      {/* Section A — Consumer Instructions */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Instructions for Consumers</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Where they should go, parking, entrance, pickup counter, special notes.</p>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={6}
            data-testid="consumer-instructions-input"
            value={data.consumer_pickup_instructions}
            onChange={(e) => setField('consumer_pickup_instructions', e.target.value)}
            placeholder={'• Where to go\n• Parking guidance\n• Entrance instructions\n• Pickup shelf or counter location\n• Any special notes'}
          />
        </CardContent>
      </Card>

      {/* Section B — Driver Instructions */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-amber-600" /> Instructions for Delivery Drivers</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Loading zones, back-door access, security desk, call-on-arrival.</p>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={6}
            data-testid="driver-instructions-input"
            value={data.driver_delivery_instructions}
            onChange={(e) => setField('driver_delivery_instructions', e.target.value)}
            placeholder={'• Where drivers should go\n• Parking / loading zone\n• Back-door or side-door access\n• Security desk notes\n• Call-on-arrival preferences'}
          />
        </CardContent>
      </Card>

      {/* Section C — Order Prep Settings */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-emerald-600" /> Order Preparation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Order Prep Time</Label>
              <span className="text-2xl font-bold text-emerald-600" data-testid="prep-time-display">{data.prep_time_minutes} min</span>
            </div>
            <Slider
              value={[data.prep_time_minutes]}
              min={5}
              max={120}
              step={5}
              onValueChange={(v) => setField('prep_time_minutes', v[0])}
              data-testid="prep-time-slider"
            />
            <p className="text-xs text-gray-500 mt-1">How long it takes you to prepare an average order.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Pickup Window Length</Label>
              <span className="text-2xl font-bold text-emerald-600" data-testid="pickup-window-display">{data.pickup_window_minutes} min</span>
            </div>
            <Slider
              value={[data.pickup_window_minutes]}
              min={15}
              max={120}
              step={15}
              onValueChange={(v) => setField('pickup_window_minutes', v[0])}
              data-testid="pickup-window-slider"
            />
            <p className="text-xs text-gray-500 mt-1">How long the consumer has to pick up after the order is ready.</p>
          </div>

          <div>
            <Label htmlFor="cutoff">Daily Order Cutoff Time (optional)</Label>
            <Input
              id="cutoff"
              type="time"
              value={data.order_cutoff_time || ''}
              onChange={(e) => setField('order_cutoff_time', e.target.value)}
              data-testid="cutoff-time-input"
              className="max-w-xs"
            />
            <p className="text-xs text-gray-500 mt-1">After this time, no new same-day orders are accepted.</p>
          </div>
        </CardContent>
      </Card>
    </SettingsPageShell>
  );
}
